// دیالوگ‌های پرداخت فاز ۲ (AP-BIL-04): ثبت چک و استعلام صیاد (US-BIL-103)، برنامه اقساط با پیش‌نمایش (US-BIL-101)، سوخت بدهی (US-BIL-104)، واریز نقد به
// بانک (US-BIL-044) و پرداخت با کارتخوان متصل (US-BIL-032). متن‌ها فقط از i18n؛ مبالغ ریال صحیح؛ هر POST ایجادکننده با Idempotency-Key.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilInstallmentGuarantee, type BilPosPaymentResponse } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, JalaliDate, JalaliDatePickerRHF, latinDigits, SampleBadge, toBusinessDate } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { parseRials } from './CashierPage';
import { addDays, Amount, bilKeys, rial } from './common';

/** شناسه صیادی ۱۶ رقمی (ارقام فارسی و جداکننده پذیرفته). */
export function normalizeSayyad(text: string): string {
  return latinDigits(text).replace(/[^0-9]/g, '');
}

const BANKS = ['mellat', 'melli', 'saderat', 'tejarat', 'sepah', 'pasargad', 'saman', 'parsian', 'refah', 'keshavarzi', 'maskan', 'eghtesad_novin'] as const;
const GUARANTEES: readonly Exclude<BilInstallmentGuarantee, null>[] = ['Cheque', 'PromissoryNote', 'None'];

interface ChequeDialogProps {
  open: boolean;
  patientId: string;
  amount?: number;
  invoiceIds?: string[];
  installmentPlanId?: string;
  onClose: () => void;
  onDone?: () => void;
}

/** ثبت چک (AC-BIL-103.1): شناسه صیادی، بانک، مبلغ، سررسید و صادرکننده؛ نتیجه صیاد پس از ثبت نمایش داده می‌شود. */
export function ChequeDialog({ open, patientId, amount, invoiceIds, installmentPlanId, onClose, onDone }: ChequeDialogProps) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [form, setForm] = useState({ sayyad: '', bank: 'mellat', branch: '', amount: amount ? String(amount) : '', issuer: '' });
  const dates = useForm<{ due: string | null }>({ defaultValues: { due: addDays(toBusinessDate(new Date()), 30) } });
  const sayyad = normalizeSayyad(form.sayyad);
  const register = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/cheques', {
          body: {
            patientId,
            sayyadId: sayyad,
            bankCode: form.bank,
            bankBranch: form.branch.trim() || null,
            amount: rial(parseRials(form.amount)),
            dueDate: dates.getValues('due') ?? toBusinessDate(new Date()),
            issuerName: form.issuer.trim(),
            documentId: null,
            invoiceIds: invoiceIds ?? null,
            installmentPlanId: installmentPlanId ?? null,
            note: null,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      onDone?.();
    },
  });
  const valid = sayyad.length === 16 && parseRials(form.amount) > 0 && form.issuer.trim() !== '';
  const result = register.data;
  return (
    <FormDialog
      open={open}
      titleKey="bil:cheques.register"
      onSubmit={() => valid && !result && register.mutate()}
      onClose={onClose}
      isDirty={form.sayyad !== '' && !result}
      isSubmitting={register.isPending}
      submitError={register.error}
    >
      {result ? (
        <Stack spacing={1} sx={{ pt: 1 }} data-testid="bil-cheque-result" data-status={result.status}>
          <Alert severity={result.status === 'Received' ? 'success' : 'warning'} action={result.sayyadIsSample ? <SampleBadge /> : undefined}>
            {t(`bil:sayyad_status.${result.sayyadStatus}`)} — {t(`bil:cheque_status.${result.status}`)}
          </Alert>
          <Typography variant="body2" dir="ltr">
            {result.sayyadId}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            required
            label={t('bil:cheques.sayyad')}
            value={form.sayyad}
            onChange={(event) => setForm({ ...form, sayyad: event.target.value })}
            error={form.sayyad !== '' && sayyad.length !== 16}
            helperText={t('bil:cheques.sayyad_help')}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-cheque-sayyad' } }}
          />
          <TextField select label={t('bil:cheques.bank')} value={form.bank} onChange={(event) => setForm({ ...form, bank: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'bil-cheque-bank' } }}>
            {BANKS.map((bank) => (
              <MenuItem key={bank} value={bank}>
                {t(`bil:bank.${bank}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('bil:cheques.bank_branch')} value={form.branch} onChange={(event) => setForm({ ...form, branch: event.target.value })} />
          <TextField
            required
            label={t('bil:cashier.amount')}
            value={form.amount}
            onChange={(event) => setForm({ ...form, amount: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-cheque-amount' } }}
          />
          <JalaliDatePickerRHF control={dates.control} name="due" labelKey="bil:cheques.due" />
          <TextField
            required
            label={t('bil:cheques.issuer')}
            value={form.issuer}
            onChange={(event) => setForm({ ...form, issuer: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'bil-cheque-issuer' } }}
          />
        </Stack>
      )}
    </FormDialog>
  );
}

interface InstallmentPlanDialogProps {
  open: boolean;
  invoiceId: string;
  onClose: () => void;
  onDone?: (planId: string) => void;
}

/** تعریف برنامه اقساط (AC-BIL-101.1) با پیش‌نمایش سررسیدها و مبلغ اقساط؛ پیش‌پرداخت = پرداخت‌شده صورتحساب (دست‌کم ۳۰٪). */
export function InstallmentPlanDialog({ open, invoiceId, onClose, onDone }: InstallmentPlanDialogProps) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [form, setForm] = useState({ count: '6', interval: '1', guarantee: 'Cheque' as Exclude<BilInstallmentGuarantee, null>, fee: '', lateFee: '' });
  const dates = useForm<{ first: string | null }>({ defaultValues: { first: addDays(toBusinessDate(new Date()), 30) } });
  const first = dates.watch('first');
  const body = {
    invoiceId,
    installmentCount: Number(latinDigits(form.count)) || 0,
    intervalMonths: Number(latinDigits(form.interval)) || 1,
    firstDueDate: first ?? toBusinessDate(new Date()),
    guarantee: form.guarantee,
    fee: form.fee ? rial(parseRials(form.fee)) : null,
    lateFee: form.lateFee ? rial(parseRials(form.lateFee)) : null,
    notes: null,
  };
  const preview = useQuery({
    queryKey: [...bilKeys.plans(session?.tenantId, 'preview'), JSON.stringify(body)],
    queryFn: () => unwrap(tenantApi.POST('/api/v1/billing/installment-plans:preview', { body })),
    enabled: open && invoiceId !== '' && body.installmentCount > 0,
    retry: false,
  });
  const create = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/installment-plans', { body, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (plan) => {
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      onDone?.(plan.id);
      onClose();
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="bil:installments.new"
      maxWidth="md"
      onSubmit={() => preview.data && create.mutate()}
      onClose={onClose}
      isDirty
      isSubmitting={create.isPending}
      submitError={create.error}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('bil:installments.count')}
            value={form.count}
            onChange={(event) => setForm({ ...form, count: event.target.value })}
            helperText={t('bil:installments.count_help')}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-plan-count' } }}
          />
          <TextField label={t('bil:installments.interval')} value={form.interval} onChange={(event) => setForm({ ...form, interval: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
          <JalaliDatePickerRHF control={dates.control} name="first" labelKey="bil:installments.first_due" />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select label={t('bil:installments.guarantee')} value={form.guarantee} onChange={(event) => setForm({ ...form, guarantee: event.target.value as Exclude<BilInstallmentGuarantee, null> })}>
            {GUARANTEES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:guarantee.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('bil:installments.fee')} value={form.fee} onChange={(event) => setForm({ ...form, fee: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-plan-fee' } }} />
          <TextField label={t('bil:installments.late_fee')} value={form.lateFee} onChange={(event) => setForm({ ...form, lateFee: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
        </Stack>
        {preview.error ? <ErrorState error={preview.error} compact /> : null}
        {preview.data ? (
          <Stack spacing={1} data-testid="bil-plan-preview">
            <Typography variant="body2">
              {t('bil:installments.min_down')} <Amount money={preview.data.minDownPayment} /> · {t('bil:installments.paid')} <Amount money={preview.data.paid} /> · {t('bil:installments.financed')}{' '}
              <Amount money={preview.data.financed} testId="bil-plan-financed" />
            </Typography>
            <Table size="small" aria-label={t('bil:installments.schedule')}>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('bil:installments.due')}</TableCell>
                  <TableCell>{t('bil:cashier.amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.data.installments.map((item) => (
                  <TableRow key={item.sequence} data-testid="bil-plan-installment">
                    <TableCell>{item.sequence}</TableCell>
                    <TableCell>
                      <JalaliDate value={item.dueDate} />
                    </TableCell>
                    <TableCell>
                      <Amount money={item.amount} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        ) : null}
      </Stack>
    </FormDialog>
  );
}

interface WriteOffDialogProps {
  open: boolean;
  patientId: string;
  invoiceIds?: string[];
  onClose: () => void;
}

/** سوخت بدهی با دلیل (AC-BIL-104.1؛ bil.debt.write_off). */
export function WriteOffDialog({ open, patientId, invoiceIds, onClose }: WriteOffDialogProps) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [reason, setReason] = useState('');
  const writeOff = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/billing/write-offs', { body: { patientId, invoiceIds: invoiceIds ?? null, reason: reason.trim() }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      setReason('');
      onClose();
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="bil:write_off.title"
      onSubmit={() => reason.trim().length >= 3 && writeOff.mutate()}
      onClose={onClose}
      isDirty={reason !== ''}
      isSubmitting={writeOff.isPending}
      submitError={writeOff.error}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="warning">{t('bil:write_off.warning')}</Alert>
        <TextField
          required
          multiline
          minRows={2}
          label={t('bil:write_off.reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'bil-write-off-reason' } }}
        />
      </Stack>
    </FormDialog>
  );
}

interface BankDepositDialogProps {
  open: boolean;
  registerId: string;
  branchId: string;
  onClose: () => void;
}

/** واریز نقد صندوق به بانک (AC-BIL-044.1): صندوق، مبلغ، حساب بانکی، تاریخ و شماره فیش. */
export function BankDepositDialog({ open, registerId, branchId, onClose }: BankDepositDialogProps) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [form, setForm] = useState({ amount: '', account: '', slip: '' });
  const dates = useForm<{ on: string | null }>({ defaultValues: { on: toBusinessDate(new Date()) } });
  const accounts = useQuery({
    queryKey: bilKeys.bankAccounts(session?.tenantId, branchId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/bank-accounts', { params: { query: { branchId } } })),
    enabled: open,
  });
  const deposits = useQuery({
    queryKey: bilKeys.bankDeposits(session?.tenantId, registerId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/bank-deposits', { params: { query: { registerId } } })),
    enabled: open && registerId !== '',
  });
  const deposit = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/bank-deposits', {
          body: { registerId, amount: rial(parseRials(form.amount)), bankAccountId: form.account, slipNumber: form.slip.trim(), depositedOn: dates.getValues('on') },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      setForm({ amount: '', account: '', slip: '' });
      onClose();
    },
  });
  const valid = parseRials(form.amount) > 0 && form.account !== '' && form.slip.trim() !== '';
  return (
    <FormDialog
      open={open}
      titleKey="bil:bank_deposit.title"
      onSubmit={() => valid && deposit.mutate()}
      onClose={onClose}
      isDirty={form.amount !== ''}
      isSubmitting={deposit.isPending}
      submitError={deposit.error}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {deposits.data?.availableCash ? (
          <Typography variant="body2" data-testid="bil-bank-deposit-available">
            {t('bil:bank_deposit.available')} <Amount money={deposits.data.availableCash} />
          </Typography>
        ) : null}
        <TextField
          required
          label={t('bil:cashier.amount')}
          value={form.amount}
          onChange={(event) => setForm({ ...form, amount: event.target.value })}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-bank-deposit-amount' } }}
        />
        <TextField
          select
          required
          label={t('bil:bank_deposit.account')}
          value={form.account}
          onChange={(event) => setForm({ ...form, account: event.target.value })}
          slotProps={{ htmlInput: { 'data-testid': 'bil-bank-deposit-account' } }}
        >
          {(accounts.data?.items ?? []).map((account) => (
            <MenuItem key={account.id} value={account.id}>
              {account.title} <bdi dir="ltr">{account.maskedNumber}</bdi>
            </MenuItem>
          ))}
        </TextField>
        <JalaliDatePickerRHF control={dates.control} name="on" labelKey="bil:bank_deposit.date" />
        <TextField
          required
          label={t('bil:bank_deposit.slip')}
          value={form.slip}
          onChange={(event) => setForm({ ...form, slip: event.target.value })}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-bank-deposit-slip' } }}
        />
      </Stack>
    </FormDialog>
  );
}

/** پرداخت مانده با کارتخوان متصل صندوق (AC-BIL-032.1، 032.2)؛ نتیجه نامعلوم در صف پیگیری شیفت. */
export function PosPaymentButton({ patientId, amount, onDone }: { patientId: string; amount: number; onDone?: (response: BilPosPaymentResponse) => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const pay = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/billing/cashier/pos-payments', { body: { patientId, amount: rial(amount), invoiceIds: null }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      onDone?.(response);
    },
  });
  const outcome = pay.data?.outcome;
  return (
    <Stack spacing={1}>
      <Button variant="outlined" onClick={() => pay.mutate()} disabled={amount <= 0 || pay.isPending} data-testid="bil-cashier-pos">
        {pay.isPending ? t('bil:pos.waiting') : t('bil:pos.pay')}
      </Button>
      {pay.data ? (
        <Alert severity={outcome === 'Approved' ? 'success' : outcome === 'Unknown' ? 'warning' : 'error'} data-testid="bil-pos-result" data-outcome={outcome} action={pay.data.isSimulated ? <SampleBadge /> : undefined}>
          {t(`bil:pos.outcome.${outcome}`, { defaultValue: t('bil:pos.outcome.Declined') })}
          {pay.data.receiptNumber ? ` · ${pay.data.receiptNumber}` : ''}
        </Alert>
      ) : null}
      {pay.error ? <ErrorState error={pay.error} compact /> : null}
    </Stack>
  );
}
